import React from "react";

export type SnippetItem = {
  url: string;
  snippet: string;
  matchStart?: number;
  matchLength?: number;
  position?: number;
  anchorId: string;
  heading?: string;
  isTitleMatch?: boolean;
  isHeadingMatch?: boolean;
  isTableMatch?: boolean;
};

export type SearchResults = {
  query: string;
  items: SnippetItem[];
};

export const isIgnorableSearchQuery = (query: string) => {
  const trimmedQuery = query.trim();

  if (!trimmedQuery) {
    return true;
  }

  return /^[#|*\s]+$/.test(trimmedQuery);
};

export function useHelpSearch(urls: string[]) {
  const workerRef = React.useRef<Worker | null>(null);
  const requestIdRef = React.useRef(0);
  const [ready, setReady] = React.useState(false);
  const [isSearching, setIsSearching] = React.useState(false);
  const [results, setResults] = React.useState<SearchResults>({ query: "", items: [] });

  React.useEffect(() => {
    if (!urls.length) return;

    const worker = new Worker(new URL("./help-search.worker.ts", import.meta.url), { type: "module" });
    workerRef.current = worker;

    // oxlint-disable-next-line unicorn/require-post-message-target-origin -- Worker.postMessage has no targetOrigin parameter
    worker.postMessage({ type: "INIT", payload: { urls } });

    const handleMessage = (event: MessageEvent) => {
      const { type, payload } = event.data;

      if (type === "READY") {
        setReady(true);
      }

      if (type === "RESULTS") {
        const { requestId, query, items } = payload;

        if (requestId !== requestIdRef.current) {
          return;
        }

        setResults({ query, items });
        setIsSearching(false);
      }
    };

    worker.addEventListener("message", handleMessage);

    return () => {
      worker.removeEventListener("message", handleMessage);
      worker.terminate();
    };
  }, [urls]);

  const search = React.useCallback((query: string) => {
    if (!workerRef.current) return;

    const trimmedQuery = query.trim();

    if (isIgnorableSearchQuery(trimmedQuery)) {
      requestIdRef.current += 1;
      setResults({ query: trimmedQuery, items: [] });
      setIsSearching(false);
      return;
    }

    if (!trimmedQuery) {
      requestIdRef.current += 1;
      setResults({ query: "", items: [] });
      setIsSearching(false);
      return;
    }

    const requestId = ++requestIdRef.current;
    setResults({ query: trimmedQuery, items: [] });
    setIsSearching(true);

    // oxlint-disable-next-line unicorn/require-post-message-target-origin -- Worker.postMessage has no targetOrigin parameter
    workerRef.current.postMessage({ type: "SEARCH", payload: { query: trimmedQuery, requestId } });
  }, []);

  return { ready, results, search, isSearching };
}
